import { Badge, Tooltip } from '@customerio/pluma-components/react';
import { ConnectionStatus, ConnectionStatusText } from '../../catalog/enums';

/** Status-dot colors (the indicator variant supports a narrower palette). */
const ConnectionStatusColor: Record<ConnectionStatus, 'red' | 'green' | 'yellow' | 'grey' | 'neutral-outline'> = {
  [ConnectionStatus.connected]: 'green',
  [ConnectionStatus.disabled]: 'grey',
  [ConnectionStatus.disconnected]: 'grey',
  [ConnectionStatus.needsAttention]: 'yellow',
  [ConnectionStatus.noRecentData]: 'yellow',
  [ConnectionStatus.error]: 'red',
  [ConnectionStatus.authenticationError]: 'red',
  [ConnectionStatus.configurationError]: 'red',
  [ConnectionStatus.multiple]: 'neutral-outline',
};

type IntegrationStatusProps = {
  status: ConnectionStatus;
  description?: string;
};

/**
 * A connection-status badge with a colored dot (except `multiple`, which is an
 * outline badge with no dot). Renders nothing for an unmapped status. A
 * description shows as a hover tooltip.
 */
export function IntegrationStatus({ status, description }: IntegrationStatusProps) {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const statusText = ConnectionStatusText[status] as string | undefined;
  if (statusText == null || statusText === '') {
    return null;
  }

  const color = status in ConnectionStatusColor ? ConnectionStatusColor[status] : 'grey';
  const hasTooltip = description != null && description.length > 0;

  const badge =
    status === ConnectionStatus.multiple ? (
      <Badge
        data-test-integrations-status={status}
        color="neutral-outline"
        style={hasTooltip ? { cursor: 'help' } : undefined}
      >
        {statusText}
      </Badge>
    ) : (
      <Badge
        data-test-integrations-status={status}
        showStatusIndicator
        color={
          // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
          color as 'red' | 'green' | 'yellow' | 'grey'
        }
        style={hasTooltip ? { cursor: 'help' } : undefined}
      >
        {statusText}
      </Badge>
    );

  if (!hasTooltip) {
    return badge;
  }

  return <Tooltip content={description}>{badge}</Tooltip>;
}
